// The stable deep links of the console (CHT-767). One place builds every link to a business object, so a record that shows another record
// (an attention item, a request that names its customer) links to the same address everywhere. An id is encoded, so it can never add a
// path segment or a query. A link grants nothing: the page and the API check the role and the relationship again.
//
// Only addresses that exist are listed. A payment, an import row or a diagnostic record gets its builder when its page exists
// (`docs/engineering/admin-ia.md`, "Deep links").

const id = (value: string): string => encodeURIComponent(value);

export const deepLinks = {
  /** The staff workspace with one conversation open. */
  conversation: (conversationId: string): string => `/concierge/${id(conversationId)}`,
  /** The inbox with a filter kept in the address (`?filter=`). */
  inbox: (filter: string): string => `/concierge?filter=${id(filter)}`,
  /** One request: the context, the history and the commands. */
  request: (requestId: string): string => `/requests/${id(requestId)}`,
  /** The request queue with a filter kept in the address (`?filter=`). */
  requestQueue: (filter: string): string => `/requests?filter=${id(filter)}`,
  /** The memory workspace of one customer. */
  customerMemory: (customerId: string): string => `/customers/${id(customerId)}/memory`,
  /** The itinerary inspector of one trip. */
  tripItinerary: (tripId: string): string => `/trips/${id(tripId)}/itinerary`,
  /** One payment (CHT-759). The area is behind the `payments` feature. */
  payment: (paymentRequestId: string): string => `/payments/${id(paymentRequestId)}`,
  /** The support view of one customer: who they are, who covers them, what is open (CHT-768). */
  customerSupport: (customerId: string): string => `/customers/${id(customerId)}`,
  /** The privacy requests of one customer: export and deletion (supervisors and administrators, CHT-771). */
  customerPrivacy: (customerId: string): string =>
    `/customers/privacy?customerId=${id(customerId)}`,
  /** The cover of one customer (supervisors, CHT-768). */
  customerCoverage: (customerId: string): string => `/coverage/customers/${id(customerId)}`,
  /** The Analytics cockpit of one customer (CHT-2027). `search` keeps the tab and the range (`?tab=events&range=90d`). */
  customerAnalytics: (customerId: string, search = ""): string =>
    `/analytics/${id(customerId)}${search}`,
  /** One import. */
  catalogImport: (importId: string): string => `/imports/${id(importId)}`,
  /** One catalog item. */
  catalogItem: (itemId: string): string => `/catalog/items/${id(itemId)}`,
  /** The coverage gaps of every customer (supervisors, CHT-768). */
  coverage: (): string => "/coverage",
  /** The payments that need reconciling (the Payments list with its filter in the address). */
  paymentsToReconcile: (): string => "/payments?filter=NEEDS_RECONCILIATION",
  /** The failed background jobs, listed on Operations. A job intent has no page of its own. */
  failedJobs: (): string => "/operations?state=FAILED",
  /** The open payment exceptions, listed and resolved on the Diagnostics page. */
  paymentExceptions: (): string => "/operations/diagnostics#payment-exceptions",
} as const;
